/**
 * 이상한 방 - 봉투 표(등급 가중치 · 풀 · 가격 · 피티 · 환급)
 *
 * 근거 문서: 로드맵 확정안 3.2절(봉투) · 3.6절(페이월 금지 준수) · 4절 8·9번(하드 피티 · 정직한 바늘)
 *           `이상한방-상태모델-v0.1.md` 11.4절(랭킹도 결제도 없으니 예측의 ROI는 음수)
 *
 * ## 확률의 원본은 이 파일 하나다
 *
 * 화면에 나가는 확률 문자열("N 60%")은 **여기 상수에서만** 만들어진다(`gradeOddsLabel`).
 * 연출 코드가 숫자를 손으로 적기 시작하면 표를 고친 날 화면이 거짓말을 하고, 확률 표시 의무가 붙는
 * 지역에서는 그것이 곧 위반이다. `economy.test.ts`가 1만 회 표본으로 이 표와 실제 빈도를 대조한다.
 *
 * ## 풀 안의 무게 - 꾸밈을 늘려도 경제가 흔들리지 않게
 *
 * 한 등급 안의 항목은 **무게(`weight`) 비율로** 뽑는다. 처음에는 균등이었는데, 꾸밈을 열 배로 늘리자
 * 그 모델이 조용히 무너졌다: N 풀이 열 줄일 때 탄창·핀은 다섯 중 하나(20%)였지만, 꾸밈 여든넷을 같은
 * 풀에 넣으면 같은 두 줄이 1.8%가 된다. **아무도 경제를 고치지 않았는데 경제가 바뀐다** - 목록의
 * 길이가 곧 확률이면 목록을 늘리는 것이 곧 밸런스 변경이다. 무게를 두면 "탄창·핀이 N의 20%"가
 * 목록 길이의 부산물이 아니라 **적어 둔 수**가 되고, 꾸밈을 더 넣어도 그 수는 그대로다.
 *
 * 난수 소비량은 그대로 둘이다(등급 롤 1 + 항목 롤 1). 무게는 그 한 롤을 누적 합으로 걷을 뿐이다.
 *
 * ## 가중치는 정수이고 합이 1000이다
 *
 * 소수 확률(0.6, 0.3, 0.09, 0.01)을 더하면 부동소수점이 1이 안 된다. 1000분율 정수면 합 검사가
 * 정확하고, 바늘 연출의 띠 폭(60 : 30 : 9 : 1)도 정수 비율 그대로다.
 *
 * ## 소프트 피티·10장 할인·10장 보장이 없는 이유 (로드맵 6절)
 *
 * 소프트 피티(연속 실패 시 확률이 조금씩 오르는 것)는 표시된 확률을 거짓으로 만든다.
 * 10장 할인은 대량 소비 유도라 페이월의 사촌이고, 10장 보장은 "10장을 한 번에 사야 할 이유"를 만든다.
 * 그래서 10장은 정확히 1장의 열 배(300)이고 연출 길이만 다르다. 피티는 **하드만** 둘이다 -
 * SR 없이 10장이면 11장째는 SR 이상, SSR 없이 60장이면 61장째는 SSR. 게이지로 화면에 보인다.
 *
 * ## 가치 0인 봉투가 없다
 *
 * 꾸밈·별명은 두 번 나오면 등급별 핀으로 바꿔 준다. 탄창은 99를 넘는 분량이 핀이 된다.
 * 따라서 어떤 봉투도 "아무것도 아니었다"로 끝나지 않는다 - `economy.test.ts`가 풀의 모든 항목을
 * 이미 다 가진 사람에게 적용해 보고 값이 0이 아님을 확인한다.
 *
 * ## 진행을 막는 산출물이 없다
 *
 * 산출물의 종류는 다섯(`REWARD_KINDS`)으로 닫혀 있고 그 안에 단계 해금·역할·참가권 같은 것은 없다.
 * `progress.isUnlocked`는 이 파일을 알지 못한다. `MagazineSource 'purchase'`는 미구현 그대로 둔다 -
 * 결제가 붙는 날 그 등록이 첫 줄이고, 그때도 살 수 있는 것은 핀뿐이다.
 */

import { MAX_MAGAZINE_COUNT } from '../magazines'
import { KITS, isKitId, type KitId } from './kits'
import { LOOKS, isLookId, obtainableLooks, type LookId } from './looks'
import { completionNicknames, isNicknameId, obtainableNicknames, type NicknameId } from './titles'

/* --- 등급 -------------------------------------------------------------------- */

/** 등급 넷. **낮은 것부터** - 바늘 띠, 피티의 "이상", 환급의 단조 증가가 전부 이 순서에 기댄다. */
export const GRADES = ['common', 'uncommon', 'rare', 'legend'] as const

export type Grade = (typeof GRADES)[number]

export function isGrade(value: unknown): value is Grade {
  return typeof value === 'string' && (GRADES as readonly string[]).includes(value)
}

/** 등급의 서열. 클수록 높다. 피티의 "SR **이상**"은 이 값으로 비교한다. */
export function gradeRank(grade: Grade): number {
  return GRADES.indexOf(grade)
}

/**
 * 등급 이름의 i18n 키. 문구는 `i18n/ko.ts`·`en.ts`에 있고 여기는 키 **이름**만 만든다(`kits.ts`의 `nameKey`와
 * 같은 결정 - 데이터가 키를 들고 화면이 `t()`를 부른다). 화면 코드가 `'grade.' + grade`를 직접 조립하지
 * 않게 하려는 것이다. 조립 자리가 여럿이면 하나가 `rarity.`로 어긋나는 날 그 자리만 `[[key]]`가 뜬다.
 */
export function gradeNameKey(grade: Grade): `grade.${Grade}` {
  return `grade.${grade}`
}

/** 가중치 합. 1000분율이라 확률 표시가 소수점 한 자리까지 정확하다. */
export const WEIGHT_TOTAL = 1000

/* --- 산출물 ------------------------------------------------------------------ */

/** 산출물 종류 전부. 닫힌 목록이라는 사실이 "진행을 막는 것이 없다"의 표현이다. */
export const REWARD_KINDS = ['magazines', 'pins', 'kit', 'look', 'nickname'] as const

export type RewardKind = (typeof REWARD_KINDS)[number]

export type Reward =
  | Readonly<{ kind: 'magazines'; count: number }>
  | Readonly<{ kind: 'pins'; amount: number }>
  | Readonly<{ kind: 'kit'; kitId: KitId; count: number }>
  | Readonly<{ kind: 'look'; lookId: LookId }>
  | Readonly<{ kind: 'nickname'; nicknameId: NicknameId }>

/**
 * 풀 한 줄. `weight`는 **같은 등급 안에서의 상대 비율**이고 합이 얼마든 상관없다(비율만 쓴다).
 *
 * 꾸밈·별명은 전부 1이다 - 하나를 다른 것보다 귀하게 만들려면 등급을 올리는 것이지 같은 등급 안에서
 * 몰래 낮추는 것이 아니다(그건 화면에 표시되지 않는 확률이라 3.6절의 표시 의무를 비껴간다).
 * 1이 아닌 것은 탄창·핀·준비물뿐이고, 그 수는 "이 등급에서 꾸밈이 아닌 것이 몇 %인가"를 적어 둔 것이다.
 */
export type PoolEntry = Readonly<{ weight: number; reward: Reward }>

/** 꾸밈·별명 한 줄. 전부 무게 1이라 id만 적는다 - 풀 표가 290줄이 되면 읽을 수 없다. */
const look = (lookId: LookId): PoolEntry => ({ weight: 1, reward: { kind: 'look', lookId } })
const nick = (nicknameId: NicknameId): PoolEntry => ({ weight: 1, reward: { kind: 'nickname', nicknameId } })

/* --- 표 ---------------------------------------------------------------------- */

export type EnvelopeTable = Readonly<{
  /** 등급 가중치(1000분율). 합이 `WEIGHT_TOTAL`이어야 한다. */
  weights: Readonly<Record<Grade, number>>
  /** 등급별 풀. 한 등급 안에서는 **무게 비율**이다 - 항목 롤 하나로 고른다(머리말). */
  pools: Readonly<Record<Grade, readonly PoolEntry[]>>
  /** 가격(핀). `ten`은 `single`의 정확히 열 배다(할인 없음). */
  price: Readonly<{ single: number; ten: number }>
  /** 하드 피티 임계. "SR 없이 이만큼 열면 그 다음 장은 SR 이상"(수를 `N`으로 적지 않는다 - 이제 등급 이름이다). */
  pity: Readonly<{ rareAfter: number; legendAfter: number }>
  /** 중복 꾸밈·별명의 핀 환급(등급별). 등급이 오를수록 커야 한다. */
  refund: Readonly<Record<Grade, number>>
  /** 탄창 상한(99)을 넘는 한 개가 핀으로 바뀌는 값. N 풀의 "탄창 1 ↔ 핀 10"과 같은 교환비다. */
  magazineOverflowRefund: number
}>

/**
 * 등급마다 **꾸밈이 아닌 것**이 차지해야 하는 몫. 무게를 정한 근거이자 테스트가 대조하는 값이다.
 *
 * 세 수는 꾸밈이 스물셋이던 시절 표에서 그대로 읽어 온 것이다(N은 열 줄 중 둘 = 20%, R은 열넷 중 다섯,
 * SR은 열셋 중 다섯). 카탈로그가 열 배가 되어도 이 몫이 그대로여야 "아이템을 늘렸을 뿐 경제는 그대로"가
 * 참이 된다. 정수 무게로는 소수점까지 맞출 수 없으므로 테스트는 1퍼센트포인트 안이면 통과로 본다.
 *
 * SSR은 꾸밈뿐이다 - 여기서 탄창이 나오면 그 봉투는 "SSR인데 탄창"이 되고, 그건 확률표가 약속한 것과
 * 사람이 받은 것이 어긋나는 유일한 자리다.
 */
export const NON_COSMETIC_SHARE: Readonly<Record<Grade, number>> = {
  common: 0.2,
  uncommon: 0.357,
  rare: 0.385,
  legend: 0,
}

/** 열 장 묶음의 크기. 가격 검사와 연출이 같은 수를 본다. */
export const TEN_PACK_SIZE = 10

/* --- 등급별 꾸밈·별명 목록 -------------------------------------------------------- */

/*
 * 어느 등급에서 나오는가는 **이 표가** 정한다 - 꾸밈·별명 데이터에 등급을 적어 두면 같은 사실을
 * 둘이 말하게 된다(`titles.ts` 머리말). 목록이 길어져도 표에 두는 이유는 이 파일 하나를 읽으면
 * 확률 모델 전체를 알 수 있어야 하기 때문이다(머리말 "확률의 원본은 이 파일 하나다").
 *
 * 빠뜨림과 겹침은 손으로 막지 않는다 - `validateEnvelopeTable`이 양방향으로 대조한다:
 * 목록에 없는 꾸밈은 "얻을 방법이 없는 데이터", 두 등급에 있는 꾸밈은 "환급액이 둘이 된다"로 잡힌다.
 */

/** N - 움직이지 않는 표식과 이름표 색. 라운드 중 제일 자주 보이는 두 자리다. */
const COMMON_LOOKS: readonly LookId[] = [
  'markerCircle', 'markerCross', 'markerTriangle', 'markerStar',
  'markerSquare', 'markerBlank', 'markerNestedSquare', 'markerRuled',
  'markerLattice', 'markerHatch', 'markerShade', 'markerDogEar',
  'markerPunched', 'markerCornerFill', 'markerDiamond', 'markerNestedDiamond',
  'markerBullseye', 'markerRing', 'markerHalfMoon', 'markerMissingPiece',
  'markerWedge', 'markerCone', 'markerStarSix', 'markerStarEight',
  'markerStarSolid', 'markerStarTwelve', 'markerPinwheel', 'markerStarRinged',
  'markerStarTeardrop', 'markerStarHollow', 'markerStarPlump', 'markerStarBead',
  'markerWhirl', 'markerBlade', 'markerFlowerSix', 'markerFlower',
  'markerFlowerWhite', 'markerFlowerEight', 'markerSnowflake', 'markerFrostFlake',
  'markerArrowUp', 'markerArrowDown', 'markerArrowRewind', 'markerArrowSwap',
  'markerArrowDouble', 'markerInfinity', 'markerThreeBars', 'markerThreeDots',
  'markerThickPlus', 'markerFullBlock', 'markerHalfBlock', 'markerShadeBlock',
  'tagMint', 'tagCoral', 'tagSky', 'tagLilac',
  'tagRose', 'tagStrawberry', 'tagCherry', 'tagPeach',
  'tagApricot', 'tagCopper', 'tagIvory', 'tagChartreuse',
  'tagMugwort', 'tagSprout', 'tagMoss', 'tagGrass',
  'tagPea', 'tagGreen', 'tagEmerald', 'tagCeladon',
  'tagJade', 'tagTeal', 'tagAqua', 'tagOcean',
  'tagAsh', 'tagSilver', 'tagIndigo', 'tagCornflower',
  'tagNavy', 'tagHydrangea', 'tagIris', 'tagWisteria',
  'tagViolet', 'tagGrape', 'tagAmethyst', 'tagPlum',
  'tagPeony', 'tagRouge', 'tagFuchsia', 'tagBlossom',
]

/** R - 스탬프 문구 세트. */
const UNCOMMON_LOOKS: readonly LookId[] = [
  'stampDetective', 'stampDeadpan', 'stampLoud', 'stampRookie',
  'stampClerk', 'stampGuard', 'stampCourier', 'stampTeacher',
  'stampBoss', 'stampReporter', 'stampCleaner', 'stampLibrarian',
  'stampScared', 'stampPolite', 'stampSleepy', 'stampHasty',
  'stampKind', 'stampBored', 'stampFlustered', 'stampShy',
  'stampGrumpy', 'stampPlayByPlay', 'stampBreakingNews', 'stampAd',
  'stampAnnouncement', 'stampPoem', 'stampFolkTale', 'stampManual',
  'stampDiary', 'stampMachine',
]

/** R - 별명. */
const UNCOMMON_NICKS: readonly NicknameId[] = [
  'emptySeatSpotter', 'doorRegular', 'chairCounter', 'cornerWatcher',
  'firstShotFirst', 'lastSecondFinder', 'sameRouteEveryTime', 'alwaysClockwise',
  'oneLapOnArrival', 'windowsFirst', 'looksUpToo', 'eyesOnTheFloor',
  'behindTheFurniture', 'tiltsTheHeadFirst', 'rightUpClose', 'takesAStepBack',
  'holdsABreath', 'doubtsItFirst', 'staresAtWhatStayed', 'sameSpotTwice',
  'countsTheShotsFirst', 'alwaysOneLeft', 'onlyWhenSure', 'allAtOnce',
  'shootAndMoveOn', 'threeThenABreather', 'keepsLookingAnyway', 'originalRoomAgain',
  'usesEverySecond', 'staysForTheResults', 'straightBackIn', 'framesLevel',
  'plantFriend', 'lampOnly', 'drawerGap', 'rugCorner',
  'clockHands', 'cupHandle', 'cushionSquare', 'bookSpines',
  'windowLatch', 'doorknobEye', 'outletCount', 'bulbDuty',
  'mirrorToo', 'chairAngle', 'shelfLine', 'curtainFolds',
  'switchWay', 'calendarPage', 'slipperPair', 'trashBin',
  'remoteSpot', 'keysThere', 'wallpaperPattern', 'pencilOne',
  'lastOneIn', 'inNoHurry', 'skipsTheBriefing', 'cameOutEmptyHanded',
]

/** SR - 결과 테두리와 봉투 겉면. 라운드가 끝난 뒤에 보이는 자리다. */
const RARE_LOOKS: readonly LookId[] = [
  'frameTape', 'frameClip', 'frameLabel', 'frameTearLine',
  'framePushpin', 'frameManuscript', 'frameFold', 'frameGlass',
  'frameIndexTab', 'framePencil', 'frameFolder', 'frameRuler',
  'frameFilm', 'frameTwine', 'frameDoorJamb', 'frameWindowSill',
  'frameGrid', 'frameMatBoard', 'envelopeStriped', 'envelopeStamped',
  'envelopeGrid', 'envelopeLattice', 'envelopePinstripe', 'envelopeHatch',
  'envelopeDiamond', 'envelopeCheck', 'envelopeRuled', 'envelopeDoubleRule',
  'envelopeRipple', 'envelopeSpeckle', 'envelopeWoodGrain', 'envelopeCorrugated',
  'envelopeWideBand', 'envelopeHairline', 'envelopeAir', 'envelopeWindow',
  'envelopePostmark', 'envelopePerf', 'envelopeCrease', 'envelopeDoubleSeal',
  'envelopeSilver', 'envelopeCopper', 'envelopeDiagonal', 'envelopeDogEar',
]

/** SR - 별명. */
const RARE_NICKS: readonly NicknameId[] = [
  'turnedThingsNemesis', 'colorHawk', 'noMissNoFuss', 'quietObserver',
  'justWarmingUp', 'nextRoundForReal', 'theGuessLanded', 'justAFeeling',
  'luckyKindOfDay', 'rightBehindYou', 'trustsTheNeighbor', 'gaspedAlong',
  'hereForTheRoom', 'likesThePlace', 'intoTheWallpaper', 'complimentsTheLighting',
  'triesTheChair', 'wantsToTidyUp', 'wouldLiveHere', 'bigReactions',
  'clapsTheLoudest', 'thinkingOutLoud', 'laughedAndMissed', 'lostTrackOfTime',
  'eyesFirst', 'quickHands', 'fasterThanClock', 'allInOneGo',
  'rightEveryTime', 'noWastedMove', 'fastAndSure', 'firstGuessRight',
  'steadyHand', 'neverRattled', 'inNoRush',
]

/** SSR - 움직이는 표식과 금박 봉투. */
const LEGEND_LOOKS: readonly LookId[] = [
  'markerPulse', 'markerSpin', 'markerCog', 'markerWheel',
  'markerCompass', 'markerRipple', 'markerBeacon', 'markerFloat',
  'frameSealTape', 'frameSpine', 'envelopeGilt', 'envelopeStain',
  'envelopeSunburst', 'envelopeRegistered',
]

/** SSR - 별명. */
const LEGEND_NICKS: readonly NicknameId[] = [
  'visitor', 'neverBlinks', 'oneAfterAnother', 'unbrokenRun',
  'alwaysInForm', 'notAScratch', 'cleanRecord', 'cleanFinish',
  'comesFromBehind', 'turnsItAround', 'oneForTheRecord', 'goodInstincts',
  'knowsWhereToLook', 'seesItThrough', 'patientEye',
]

/**
 * 봉투 표. 3.2절의 표를 그대로 옮긴 것이고, **전부 플레이테스트 대상이다.**
 *
 * 준비물은 종류마다 한 줄씩 놓았다(R에 1개짜리 넷, SR에 2개짜리 넷). "준비물 1"을 한 항목으로 두고
 * 종류를 또 뽑으면 난수 소비가 셋이 되어 `openEnvelope`의 "등급 롤 1 + 항목 롤 1" 계약이 깨진다.
 * 넷이 같은 무게로 한 자리씩 차지하는 것이 곧 "어느 준비물이 나오는가"다.
 */
export const ENVELOPE_TABLE: EnvelopeTable = {
  weights: { common: 600, uncommon: 300, rare: 90, legend: 10 },

  pools: {
    /* N - 탄창 1 · 핀 10 · 표식(움직이지 않는 것) · 이름표 색 대부분 */
    common: [
      { weight: 12, reward: { kind: 'magazines', count: 1 } },
      { weight: 12, reward: { kind: 'pins', amount: 10 } },
      ...COMMON_LOOKS.map(look),
    ],
    /* R - 탄창 2 · 준비물 1 · 스탬프 문구 세트 · 이름표 몇 · 별명 다수 */
    uncommon: [
      { weight: 10, reward: { kind: 'magazines', count: 2 } },
      { weight: 10, reward: { kind: 'kit', kitId: 'magnifier', count: 1 } },
      { weight: 10, reward: { kind: 'kit', kitId: 'leisure', count: 1 } },
      { weight: 10, reward: { kind: 'kit', kitId: 'slowClock', count: 1 } },
      { weight: 10, reward: { kind: 'kit', kitId: 'spare', count: 1 } },
      ...UNCOMMON_LOOKS.map(look),
      ...UNCOMMON_NICKS.map(nick),
    ],
    /* SR - 탄창 3 · 준비물 2 · 결과 테두리 · 봉투 겉면 · 별명 */
    rare: [
      { weight: 10, reward: { kind: 'magazines', count: 3 } },
      { weight: 10, reward: { kind: 'kit', kitId: 'magnifier', count: 2 } },
      { weight: 10, reward: { kind: 'kit', kitId: 'leisure', count: 2 } },
      { weight: 10, reward: { kind: 'kit', kitId: 'slowClock', count: 2 } },
      { weight: 10, reward: { kind: 'kit', kitId: 'spare', count: 2 } },
      ...RARE_LOOKS.map(look),
      ...RARE_NICKS.map(nick),
    ],
    /* SSR - 움직이는 표식 · 가장 화려한 봉투 겉면과 테두리 · 별명 */
    legend: [...LEGEND_LOOKS.map(look), ...LEGEND_NICKS.map(nick)],
  },

  /*
   * 1장 30핀. 1단계 23핀 + 2단계 24핀 = 47핀이라 **첫 봉투가 첫 세션 안에 열린다**(3.1절).
   * 이후 단계당 20~30핀이라 거의 매 판 한 장이다. 10장은 정확히 300 - 할인이 없다는 것이 규칙이다.
   */
  price: { single: 30, ten: 300 },

  pity: { rareAfter: 10, legendAfter: 60 },

  /*
   * 중복 환급. N 10은 풀의 "핀 10" 항목과 같은 값이라 N 봉투의 최솟값이 10핀으로 고정되고,
   * SSR 200은 봉투 여섯 장 값이라 "SSR이 중복이었다"가 손해로 읽히지 않는다.
   */
  refund: { common: 10, uncommon: 25, rare: 60, legend: 200 },

  magazineOverflowRefund: 10,
}

/* --- 확률 표시 ---------------------------------------------------------------- */

/** 등급의 확률(%). 1000분율을 10으로 나눈 값이라 소수점 한 자리까지 정확하다(60 · 30 · 9 · 1). */
export function gradeOddsPercent(grade: Grade, table: EnvelopeTable = ENVELOPE_TABLE): number {
  return table.weights[grade] / (WEIGHT_TOTAL / 100)
}

/**
 * 화면에 나가는 확률 문자열. **연출은 이 함수만 부른다.**
 *
 * 서식이 단순한 이유는 숫자 서식이 표시 계층의 일이라서다(GLOSSARY 5절). 여기서 하는 것은
 * "표의 값에서 나왔다"를 보증하는 것뿐이고, `%` 하나만 붙인다.
 */
export function gradeOddsLabel(grade: Grade, table: EnvelopeTable = ENVELOPE_TABLE): string {
  return `${String(gradeOddsPercent(grade, table))}%`
}

/* --- 저장값 읽기 -------------------------------------------------------------- */

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function positiveInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : null
}

/**
 * 저장값 → 산출물. 모양이 아니면 `null`(그 줄만 버린다).
 *
 * 봉투 이력(`EnvelopeState.log`)이 이 함수를 거친다. 꾸밈·별명·준비물 id가 표에서 사라진 뒤에 읽히는
 * 옛 이력이 정상 입력이고, 그때 던지면 진행 저장이 통째로 빈 것이 된다(`progress.ts`의 계약).
 */
export function parseReward(value: unknown): Reward | null {
  if (!isRecordLike(value)) return null

  switch (value['kind']) {
    case 'magazines': {
      const count = positiveInteger(value['count'])
      return count === null ? null : { kind: 'magazines', count }
    }
    case 'pins': {
      const amount = positiveInteger(value['amount'])
      return amount === null ? null : { kind: 'pins', amount }
    }
    case 'kit': {
      const count = positiveInteger(value['count'])
      const kitId = value['kitId']
      return count === null || !isKitId(kitId) ? null : { kind: 'kit', kitId, count }
    }
    case 'look': {
      const lookId = value['lookId']
      return isLookId(lookId) ? { kind: 'look', lookId } : null
    }
    case 'nickname': {
      const nicknameId = value['nicknameId']
      return isNicknameId(nicknameId) ? { kind: 'nickname', nicknameId } : null
    }
    default:
      return null
  }
}

/* --- 검증 -------------------------------------------------------------------- */

/**
 * 봉투 표를 검사한다. 문제가 없으면 빈 배열(`levels.validateLevels`와 같은 모양).
 *
 * 검사하는 것: 가중치 합 · 등급별 풀 최소 1개 · 산출물 종류가 닫힌 목록 안 · id가 실재 · 값이 양수 ·
 * 꾸밈·별명이 **정확히 한 등급**에만(환급액이 등급에서 나오므로 두 곳에 있으면 값이 둘) · 기본 꾸밈이
 * 풀에 없음 · 얻을 수 있는 꾸밈·별명이 전부 어딘가의 풀에 있음(죽은 데이터 방지) · 가격은 할인 없음 ·
 * 피티 임계 양수 · 환급이 등급을 따라 오름.
 */
export function validateEnvelopeTable(table: EnvelopeTable = ENVELOPE_TABLE): readonly string[] {
  const issues: string[] = []

  const weightSum = GRADES.reduce((sum, grade) => sum + table.weights[grade], 0)
  if (weightSum !== WEIGHT_TOTAL) issues.push(`가중치 합이 ${String(WEIGHT_TOTAL)}이 아니다: ${String(weightSum)}`)
  for (const grade of GRADES) {
    const weight = table.weights[grade]
    if (!Number.isSafeInteger(weight) || weight <= 0) issues.push(`'${grade}' 가중치가 양의 정수가 아니다: ${String(weight)}`)
  }

  const lookGrades = new Map<string, Grade[]>()
  const nicknameGrades = new Map<string, Grade[]>()
  const defaultLookIds = new Set<string>(LOOKS.filter((look) => look.source === 'default').map((look) => look.id))
  /*
   * 풀에 **있으면 안 되는** 것 둘. 기본 꾸밈은 뽑는 순간 중복이라서고, 완주 보상은 다 모은 사람에게만
   * 가는 것이라 뽑혀 나오면 그 조건 자체가 무너진다(`looks.ts`·`titles.ts` 머리말).
   */
  const completeLookIds = new Set<string>(LOOKS.filter((look) => look.source === 'complete').map((look) => look.id))

  for (const grade of GRADES) {
    const pool = table.pools[grade]
    if (pool.length === 0) issues.push(`'${grade}' 풀이 비어 있다 - 이 등급이 뜨면 줄 것이 없다`)

    for (const entry of pool) {
      /*
       * 무게는 양의 정수다. 0이나 음수를 허용하면 "목록에 있는데 절대 안 나오는 줄"이 생기고,
       * 그건 얻을 방법이 없는 데이터를 잡는 아래 검사를 **통과하면서** 얻을 수 없는 상태다.
       * 소수를 막는 이유는 등급 가중치와 같다 - 비율을 정수로 적으면 합이 정확하다.
       */
      if (positiveInteger(entry.weight) === null) {
        issues.push(`'${grade}' 풀의 무게가 양의 정수가 아니다: ${String(entry.weight)}`)
      }
      const reward = entry.reward
      if (!(REWARD_KINDS as readonly string[]).includes(reward.kind)) {
        issues.push(`'${grade}' 풀에 모르는 산출물 종류가 있다: ${String(reward.kind)}`)
        continue
      }
      switch (reward.kind) {
        case 'magazines':
          if (positiveInteger(reward.count) === null || reward.count > MAX_MAGAZINE_COUNT) {
            issues.push(`'${grade}' 풀의 탄창 수가 1~${String(MAX_MAGAZINE_COUNT)}이 아니다: ${String(reward.count)}`)
          }
          break
        case 'pins':
          if (positiveInteger(reward.amount) === null) issues.push(`'${grade}' 풀의 핀이 양의 정수가 아니다: ${String(reward.amount)}`)
          break
        case 'kit':
          if (!isKitId(reward.kitId)) issues.push(`'${grade}' 풀에 없는 준비물이 있다: ${String(reward.kitId)}`)
          if (positiveInteger(reward.count) === null) issues.push(`'${grade}' 풀의 준비물 수가 양의 정수가 아니다`)
          break
        case 'look':
          if (!isLookId(reward.lookId)) issues.push(`'${grade}' 풀에 없는 꾸밈이 있다: ${String(reward.lookId)}`)
          if (defaultLookIds.has(reward.lookId)) issues.push(`기본 꾸밈 '${reward.lookId}'이(가) 풀에 있다 - 뽑는 순간 중복이다`)
          if (completeLookIds.has(reward.lookId)) {
            issues.push(`완주 보상 '${reward.lookId}'이(가) 풀에 있다 - 다 모으기 전에 나온다`)
          }
          lookGrades.set(reward.lookId, [...(lookGrades.get(reward.lookId) ?? []), grade])
          break
        case 'nickname':
          if (!isNicknameId(reward.nicknameId)) issues.push(`'${grade}' 풀에 없는 별명이 있다: ${String(reward.nicknameId)}`)
          nicknameGrades.set(reward.nicknameId, [...(nicknameGrades.get(reward.nicknameId) ?? []), grade])
          break
      }
    }
  }

  for (const [id, grades] of lookGrades) {
    if (grades.length > 1) issues.push(`꾸밈 '${id}'이(가) 여러 등급에 있다(${grades.join(', ')}) - 환급액이 둘이 된다`)
  }
  for (const [id, grades] of nicknameGrades) {
    if (grades.length > 1) issues.push(`별명 '${id}'이(가) 여러 등급에 있다(${grades.join(', ')}) - 환급액이 둘이 된다`)
  }
  for (const look of obtainableLooks()) {
    if (!lookGrades.has(look.id)) issues.push(`꾸밈 '${look.id}'이(가) 어느 풀에도 없다 - 얻을 방법이 없는 데이터다`)
  }
  for (const nick of obtainableNicknames()) {
    if (!nicknameGrades.has(nick.id)) issues.push(`별명 '${nick.id}'이(가) 어느 풀에도 없다 - 얻을 방법이 없는 데이터다`)
  }
  for (const nick of completionNicknames()) {
    if (nicknameGrades.has(nick.id)) issues.push(`완주 보상 별명 '${nick.id}'이(가) 풀에 있다 - 다 모으기 전에 나온다`)
  }
  for (const kit of KITS) {
    const inPool = GRADES.some((grade) => table.pools[grade].some(({ reward }) => reward.kind === 'kit' && reward.kitId === kit.id))
    if (!inPool) issues.push(`준비물 '${kit.id}'이(가) 어느 풀에도 없다`)
  }

  if (positiveInteger(table.price.single) === null) issues.push(`1장 가격이 양의 정수가 아니다: ${String(table.price.single)}`)
  if (table.price.ten !== table.price.single * TEN_PACK_SIZE) {
    issues.push(`10장 가격(${String(table.price.ten)})이 1장의 ${String(TEN_PACK_SIZE)}배(${String(table.price.single * TEN_PACK_SIZE)})가 아니다 - 할인은 없다`)
  }

  if (positiveInteger(table.pity.rareAfter) === null) issues.push('SR 피티 임계가 양의 정수가 아니다')
  if (positiveInteger(table.pity.legendAfter) === null) issues.push('SSR 피티 임계가 양의 정수가 아니다')
  if (table.pity.legendAfter <= table.pity.rareAfter) issues.push('SSR 피티가 SR 피티보다 먼저 온다 - 순서가 뒤집혔다')

  let previousRefund = 0
  for (const grade of GRADES) {
    const refund = table.refund[grade]
    if (positiveInteger(refund) === null) issues.push(`'${grade}' 환급이 양의 정수가 아니다 - 가치 0인 봉투가 생긴다`)
    if (refund < previousRefund) issues.push(`'${grade}' 환급(${String(refund)})이 아래 등급보다 작다`)
    previousRefund = refund
  }
  if (positiveInteger(table.magazineOverflowRefund) === null) issues.push('탄창 초과 환급이 양의 정수가 아니다')

  return issues
}
